import React, { useEffect, useMemo, useContext} from "react";
import { useSelector } from "react-redux";

import Thumbnail from "src/components/thumbnail";
import { Player } from "../player/index";
import { SimulationReportContext} from "../../context";

import { getFullName } from "src/utils/utils";
import { Media } from "src/types/media";
import { FileService } from "src/utils/file.service";
import { CALL_TYPES, DIFFICULTY_CHOICES } from "src/constants/simulation";

import _ from "lodash";
import cx from "classnames";
import styles from "./recordings.module.scss";

interface CallRecordingProps{
  learner: any;
}

export const CallRecording: React.FC<CallRecordingProps> = ({ learner }) => {
  const reportContext = React.useContext(SimulationReportContext);

  const callType = useMemo(() => {
    const value = _.get(reportContext?.simulation, "type") || "";
    return CALL_TYPES.find((item) => item.id === value)?.title || "";
  }, [reportContext?.simulation]);

  const difficulty = useMemo(() => {
    const value = _.get(reportContext?.simulation, "difficulty") || "";
    return DIFFICULTY_CHOICES.find((item) => item.value === value)?.label || "";
  }, [reportContext?.simulation]);

  const handleToggleCallRecording = () => {
    if (reportContext.toggleCallRecording) {
      reportContext.toggleCallRecording();
    }
  };


  return (
    <div className={styles.callContainer}>
      <div className={styles.contentWrapper}>
        <div className={styles.headerContainer}>
          <div className={styles.callHeader}>
            <p className={styles.title}>call recording</p>
            <i
              className={cx("paperflite-close", styles.closeIcon)}
              onClick={handleToggleCallRecording}
            />
          </div>
          <div className={styles.options}>
            <div className={styles.metric}>
              <i className="paperflite-calendar" />
              <span className={styles.name}>{callType}</span>
            </div>
            <div className={styles.metric}>
              <i className="paperflite-meter" />
              <span className={styles.name}>{difficulty}</span>
            </div>
          </div>
        </div>
        <div className={styles.body}>
          <CallTranscripts learner={learner} />
        </div>
        <div className={styles.callRecording}>
          <Player simulation={reportContext.learningRecord} />
        </div>
      </div>
    </div>
  );
};

interface CallTranscriptsProps {
  learner: any;
}

const CallTranscripts: React.FC<CallTranscriptsProps> = ({ learner }) => {
  const reportContext = React.useContext(SimulationReportContext);

  const learnerProfile = useMemo(() => {
    const profile = _.get(learner, "user");
    return {
      name: getFullName(profile),
      icon: _.get(profile, "icon"),
    }
  }, [learner]);

  const personaProfile = useMemo(() => {
    const persona = reportContext?.simulation?.persona;
    return {
      name: persona?.name || "",
      icon: persona?.icon,
    }
  }, [reportContext?.simulation]);

  if (reportContext.fetchingTranscripts && _.size(reportContext.transcripts) === 0) {
    return (
      <div className={styles.transcripts}>
        {_.times(4).map((_, index) => (
          <TranscriptRowPlaceholder key={index} />
        ))}
      </div>
    );
  }

  return (
    <div className={styles.transcripts}>
      {(reportContext.transcripts || []).filter(t => t.message).map((transcript, index) => {
        const role = transcript.role;
        const speaker = role === "user" ? learnerProfile : personaProfile;
        return (
          <TranscriptRow
            key={index}
            speaker={speaker}
            text={transcript.message}
            time={transcript.secondsFromStart > 0 && FileService.formatTime(transcript.secondsFromStart)}
          />
        )
      })}
    </div>
  );
}


interface TranscriptRowProps {
  speaker: {
    name: string;
    icon?: Media;
  };
  text: string;
  time: string | boolean;
}

const TranscriptRow: React.FC<TranscriptRowProps> = ({ speaker, text, time }) => {
  return (
    <div className={styles.transcriptRow}>
      {time && <div className={styles.time}>{time}</div>}
      <div className={styles.speaker}>
        <Thumbnail className={styles.icon} media={speaker.icon} defaultImage="/images/assets/simulationThumbnail.jpg" />
        <div className={styles.name}>{speaker.name}</div>
      </div>
      <div className={styles.text}>{text}</div>
    </div>
  )
}

const TranscriptRowPlaceholder: React.FC = () => {
  return (
    <div className={cx(styles.transcriptRow, styles.placeholder)}>
      <div className={styles.speaker}>
        <div className={cx(styles.icon, styles.block)} />
        <div className={cx(styles.name, styles.block)}></div>
      </div>
      <div className={cx(styles.text)}>
        <div className={cx(styles.block)}></div>
        <div className={cx(styles.block)}></div>
      </div>
    </div>
  );
}